import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Chip,
  Skeleton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Add,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { campaignsService } from '../services/campaigns.service';
import { adsetsService } from '../services/adsets.service';
import { adsService } from '../services/ads.service';

interface Props {
  campaignId?: string;
  adSetId?: string;
  adId?: string;
}

const statusDot = (status: string) => {
  const colors: Record<string, string> = {
    ACTIVE: '#4caf50',
    PAUSED: '#ff9800',
    DELETED: '#f44336',
  };
  return colors[status] || '#9e9e9e';
};

export default function AdHierarchySidebar({ campaignId, adSetId, adId }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [width, setWidth] = useState(260);
  const [isResizing, setIsResizing] = useState(false);

  // Drag to resize — track delta from initial mousedown position
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    setIsResizing(true);
    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      setWidth(Math.max(180, Math.min(450, startWidth + delta)));
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width]);

  const { data: campaign, isLoading: loadingCampaign } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => campaignsService.getById(campaignId!),
    enabled: !!campaignId,
  });

  const { data: adSetsResult } = useQuery({
    queryKey: ['campaign-adsets-sidebar', campaignId],
    queryFn: () => adsetsService.query({ campaign_id: campaignId, limit: 100 }),
    enabled: !!campaignId,
  });
  const adSets = adSetsResult?.data || [];

  const { data: adsResult } = useQuery({
    queryKey: ['adset-ads-sidebar', adSetId],
    queryFn: () => adsService.query({ ad_set_id: adSetId, limit: 100 }),
    enabled: !!adSetId,
  });
  const ads = adsResult?.data || [];

  const isEditing = location.pathname.includes('/edit');

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      <Box
        sx={{
          width,
          minWidth: width,
          borderRight: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          height: '100%',
          overflow: 'auto',
          py: 0.5,
          userSelect: isResizing ? 'none' : undefined,
        }}
      >
        <Typography variant="overline" sx={{ px: 1.5, color: 'text.secondary', fontSize: '0.65rem' }}>
          Hierarchy
        </Typography>

        <List dense disablePadding sx={{ '& .MuiListItemButton-root': { py: 0.3, minHeight: 32 } }}>
          {/* Campaign */}
          {loadingCampaign ? (
            <Box sx={{ px: 1.5, py: 0.5 }}><Skeleton width="80%" height={24} /></Box>
          ) : campaign ? (
            <Tooltip title={campaign.name} placement="right" enterDelay={500}>
              <ListItemButton
                selected={!adSetId && !adId}
                onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
                sx={{ pl: 1.5 }}
              >
                <CampaignIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.75 }} />
                <ListItemText
                  primary={campaign.name}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true, fontSize: '0.8rem' }}
                />
              </ListItemButton>
            </Tooltip>
          ) : null}

          {/* Ad Sets */}
          {adSets.map((as) => (
            <Box key={as.id}>
              <Tooltip title={as.name} placement="right" enterDelay={500}>
                <ListItemButton
                  selected={as.id === adSetId && !adId}
                  onClick={() => navigate(isEditing ? `/adsets/${as.id}/edit` : `/adsets/${as.id}`)}
                  sx={{ pl: 3 }}
                >
                  <Box
                    sx={{
                      width: 7, height: 7, borderRadius: '50%', mr: 0.75, flexShrink: 0,
                      bgcolor: statusDot(as.status),
                    }}
                  />
                  <ListItemText
                    primary={as.name}
                    primaryTypographyProps={{
                      variant: 'body2', noWrap: true, fontSize: '0.78rem',
                      fontWeight: as.id === adSetId ? 600 : 400,
                    }}
                  />
                  {!as.fb_adset_id && (
                    <Chip label="Confirming" size="small" variant="outlined"
                      sx={{ height: 16, fontSize: '0.6rem', ml: 0.5, '& .MuiChip-label': { px: 0.5 } }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={as.id === adSetId} unmountOnExit>
                {ads.map((ad) => (
                  <Tooltip key={ad.id} title={ad.name} placement="right" enterDelay={500}>
                    <ListItemButton
                      selected={ad.id === adId}
                      onClick={() => navigate(isEditing ? `/ads/${ad.id}/edit` : `/ads/${ad.id}`)}
                      sx={{ pl: 4.5 }}
                    >
                      {ad.creative_image_url ? (
                        <Avatar
                          src={ad.creative_image_url}
                          variant="rounded"
                          sx={{ width: 24, height: 24, mr: 0.75, flexShrink: 0 }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 6, height: 6, borderRadius: '50%', mr: 0.75, flexShrink: 0,
                            bgcolor: statusDot(ad.status),
                          }}
                        />
                      )}
                      <ListItemText
                        primary={ad.name}
                        primaryTypographyProps={{
                          variant: 'caption', noWrap: true, fontSize: '0.72rem',
                          fontWeight: ad.id === adId ? 600 : 400,
                        }}
                      />
                      {!ad.fb_ad_id && (
                        <Chip label="Confirming" size="small" variant="outlined"
                          sx={{ height: 14, fontSize: '0.55rem', ml: 0.5, '& .MuiChip-label': { px: 0.4 } }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                ))}
                <ListItemButton
                  onClick={() => navigate(`/ads/new?adset_id=${as.id}`)}
                  sx={{ pl: 4.5, color: 'primary.main' }}
                >
                  <Add sx={{ fontSize: 14, mr: 0.75 }} color="primary" />
                  <ListItemText
                    primary="New Ad"
                    primaryTypographyProps={{ variant: 'caption', color: 'primary', fontSize: '0.72rem' }}
                  />
                </ListItemButton>
              </Collapse>
            </Box>
          ))}

          {campaignId && (
            <ListItemButton
              onClick={() => navigate(`/adsets/new?campaign_id=${campaignId}`)}
              sx={{ pl: 3, color: 'primary.main' }}
            >
              <Add sx={{ fontSize: 14, mr: 0.75 }} color="primary" />
              <ListItemText
                primary="New Ad Set"
                primaryTypographyProps={{ variant: 'body2', color: 'primary', fontSize: '0.78rem' }}
              />
            </ListItemButton>
          )}
        </List>
      </Box>

      {/* Resize handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 4,
          cursor: 'col-resize',
          flexShrink: 0,
          '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
          ...(isResizing && { bgcolor: 'primary.main', opacity: 0.3 }),
        }}
      />
    </Box>
  );
}
